import React, { useRef, useState } from 'react';
import { NSButton, NSRichTextEditor } from 'bricks';
import { MentionPluginItem } from 'bricks/NSRichTextEditor/components/MentionsPlugin/types';
import { NSRichTextEditorRef } from 'bricks/NSRichTextEditor/NSRichTextEditor';
import { InfiniteData } from '@tanstack/react-query';
import { IUser } from 'types/user';
import { useCreateNote } from 'views/DealDashboard/hooks';
import { ICreateNoteRequest } from 'views/DealDashboard/types';
import infiniteOrganizationUsersQueryOptions from '../hooks/useInfiniteOrganizationUsers';

export interface AddNoteProps {
    dealId: number;
    orgId: string;
    onClose: () => void;
    showMonthHeader?: boolean;
}

const AddNote: React.FC<AddNoteProps> = ({
    onClose, showMonthHeader = true, dealId, orgId,
}) => {
    const mentionsQueryOptions = (query: string) => ({
        ...infiniteOrganizationUsersQueryOptions(orgId, { search_value: query }),
        enabled: !!query,
        select: (data: InfiniteData<IUser[]>) => data.pages.flatMap(page => page.map(user => ({
            name: user.name,
            type: 'user',
            id: user.id,
            picture: user.picture || '',
        }))),
    });

    const editorRef = useRef<NSRichTextEditorRef>(null);
    const [isEditorEmpty, setIsEditorEmpty] = useState(true);
    const [mentions, setMentions] = useState<MentionPluginItem[]>([]);
    const [error, setError] = useState<string | null>(null);

    const handleEditorChange = () => {
        if (editorRef.current) {
            setIsEditorEmpty(editorRef.current.isEditorEmpty());
        }
    };

    const handleMentionSelect = (option: MentionPluginItem) => {
        setMentions(prev => [...prev, option]);
    };

    const { mutateAsync: createNote, isPending } = useCreateNote();

    const handleSaveContent = async () => {
        if (editorRef.current && !isEditorEmpty) {
            try {
                setError(null);
                const editorState = editorRef.current.getEditorState();
                const jsonState = editorState?.toJSON();

                const content = editorRef.current.getEditorContent();
                const newActivity: ICreateNoteRequest = {
                    organizationId: orgId,
                    dealId: +dealId,
                    title: '',
                    body: content.htmlContent,
                    mentions: mentions.map(mention => ({
                        id: mention.id,
                        name: mention.name,
                    })),
                    editorState: jsonState,
                };
                await createNote(newActivity);
                setMentions([]);
                onClose();
            } catch (err) {
                setError('Failed to save note. Please try again.');
                console.error('Error saving note:', err);
            }
        }
    };

    const currentDate = new Date().toLocaleString('default', { month: 'short', year: 'numeric' });

    return (
        <div className="w-100 px-3 pb-2">
            {showMonthHeader && (
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="font-weight-bold text-nowrap text-uppercase">
                        {currentDate}
                    </h5>
                    <hr className="ml-2 w-100" />
                </div>
            )}
            <div>
                <NSRichTextEditor
                    ref={editorRef}
                    placeholder="Type something..."
                    onChange={handleEditorChange}
                    mentionsQueryOptions={mentionsQueryOptions}
                    onSelect={handleMentionSelect}
                />
                {error && (
                    <div className="text-danger mt-2">
                        {error}
                    </div>
                )}
                <div className="d-flex justify-content-end">
                    <NSButton
                        className="mr-2"
                        color="secondary"
                        size="sm"
                        callback={onClose}
                        outline={false}
                        disabled={isPending}
                    >
                        Cancel
                    </NSButton>
                    <NSButton
                        callback={handleSaveContent}
                        color="primary"
                        size="sm"
                        outline={false}
                        disabled={isEditorEmpty || isPending}
                        className="heap-deal-activity-save-note"
                    >
                        {isPending ? 'Saving...' : 'Save note'}
                    </NSButton>
                </div>
            </div>
        </div>
    );
};

export default AddNote;
